import { useMemo } from "react";
import type { Keyboard, Layers } from "@/domain/types";
import type { SC2Table } from "@/games/sc2/parser/index";
import { placedZoneCodes } from "@/games/sc2/render-aux";
import { columnReservation, layerCodes } from "@/render/mouse-map/compose";
import { CHIP_RESERVE, MOUSE_FRAME } from "@/render/mouse-map/geometry";
import { useSurfaceUnit } from "./use-surface-unit";

/**
 * Size both render surfaces to one shared key unit, and reserve the mouse chip columns so the layout
 * never shifts. The codes a side can place: SC2 reads the table's placements (selection-
 * independent, so hover / modifier / command-card switches never reflow); a simple Profile reads its
 * layers. While comparing, reservation spans BOTH sides and the wrap stacks two board+mouse rows, so
 * it sizes to the WIDER board plus the mouse, and to the SUM of both surface heights plus the stack
 * chrome (two header cards, the inter-row gaps, and the plate-overflow padding each row reserves).
 */
export function useSurfaceSizing({
  comparing,
  table,
  tableB,
  layers,
  layersB,
  board,
  boardB,
}: {
  comparing: boolean;
  table: SC2Table | null;
  tableB: SC2Table | null;
  layers: Layers;
  layersB: Layers;
  board: Keyboard | undefined;
  boardB: Keyboard | undefined;
}) {
  const reserve = useMemo(() => {
    const sideCodes = (t: SC2Table | null, l: Layers) => (t ? placedZoneCodes(t) : layerCodes(l));
    if (!comparing) return columnReservation(sideCodes(table, layers));
    return columnReservation(new Set([...sideCodes(table, layers), ...sideCodes(tableB, layersB)]));
  }, [comparing, table, tableB, layers, layersB]);
  const mouseUnits =
    (reserve.left ? CHIP_RESERVE.left : 0) +
    MOUSE_FRAME.width +
    (reserve.right ? CHIP_RESERVE.right : 0);
  const surfaceHeightA = Math.max(board?.height ?? 1, MOUSE_FRAME.height);
  const surfaceHeightB = Math.max(boardB?.height ?? 1, MOUSE_FRAME.height);
  // the fixed chrome the two stacked surfaces must clear: both per-side header cards plus the
  // inter-row gaps and plate padding. Reserved in pixels (not scaled into heightUnits) so it does not
  // shrink with the unit and let the bottom board spill off-screen on short / zoomed windows. Sized
  // for a one-line card per side with headroom for the B side wrapping to two lines.
  const COMPARE_CHROME_PX = 184;
  const widthUnits =
    Math.max(board?.width ?? 1, comparing ? (boardB?.width ?? 1) : 0) + 0.5 + mouseUnits;
  const heightUnits = comparing ? surfaceHeightA + surfaceHeightB : surfaceHeightA;
  const { ref: wrapRef, unit } = useSurfaceUnit(
    widthUnits,
    heightUnits,
    comparing ? COMPARE_CHROME_PX : 0,
  );
  return { reserve, unit, wrapRef };
}
